import {
  isRootLikeHostedDirectoryName,
  shouldDescendHostedProjectDirectoryPath,
  shouldUploadHostedProjectFilePath,
} from "@/services/hosted-project-upload-filter"

export type HostedFolderFileHandle = {
  kind: "file"
  name: string
  getFile: () => Promise<File>
}

export type HostedFolderDirectoryHandle = {
  kind: "directory"
  name: string
  values: () => AsyncIterable<HostedFolderFileHandle | HostedFolderDirectoryHandle>
}

export type HostedFolderSelection = {
  name: string
  files: File[]
}

type DirectoryPickerGlobal = typeof globalThis & {
  showDirectoryPicker?: (options?: { mode?: "read" }) => Promise<HostedFolderDirectoryHandle>
}

export function hostedDirectoryPickerAvailable(source = globalThis as DirectoryPickerGlobal) {
  return typeof source.showDirectoryPicker === "function"
}

export function hostedFolderFileRelativePath(file: File) {
  return (file as File & { webkitRelativePath?: string }).webkitRelativePath?.trim() || file.name
}

export function hostedFolderNameFromFiles(files: File[]) {
  const first = files[0]
  if (!first) return ""
  return hostedFolderFileRelativePath(first).replace(/\\/g, "/").split("/").filter(Boolean)[0] ?? ""
}

export function isHostedFolderPickerCancel(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError"
}

export function hostedFileWithRelativePath(file: File, relativePath: string) {
  try {
    Object.defineProperty(file, "webkitRelativePath", {
      configurable: true,
      value: relativePath,
    })
    return file
  } catch {
    const copy = new File([file], file.name, { type: file.type, lastModified: file.lastModified })
    Object.defineProperty(copy, "webkitRelativePath", {
      configurable: true,
      value: relativePath,
    })
    return copy
  }
}

export async function collectHostedDirectoryFiles(
  handle: HostedFolderDirectoryHandle,
  prefix = handle.name,
): Promise<File[]> {
  const entries: Array<Promise<File[]>> = []
  for await (const entry of handle.values()) {
    const relativePath = `${prefix}/${entry.name}`
    if (entry.kind === "directory" && !shouldDescendHostedProjectDirectoryPath(relativePath)) continue
    if (entry.kind === "file" && !shouldUploadHostedProjectFilePath(relativePath)) continue
    entries.push(
      entry.kind === "directory"
        ? collectHostedDirectoryFiles(entry, relativePath)
        : entry.getFile().then((file) => [hostedFileWithRelativePath(file, relativePath)]),
    )
  }
  return (await Promise.all(entries)).flat()
}

export async function pickHostedFolderWithNativePicker(source = globalThis as DirectoryPickerGlobal) {
  const picker = source.showDirectoryPicker
  if (!picker) return null
  try {
    const directory = await picker({ mode: "read" })
    if (isRootLikeHostedDirectoryName(directory.name)) throw new Error("Choose a project folder, not a filesystem root.")
    return {
      name: directory.name,
      files: await collectHostedDirectoryFiles(directory),
    }
  } catch (error) {
    if (isHostedFolderPickerCancel(error)) return null
    throw error
  }
}
